import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { color } from '../../app/theme';
import Tooltip from '../shared/Tooltip';
import { TriggerTimeType } from '../../app/types';

const useStyles = makeStyles(
  {
    root: {
      paddingBottom: 8,
    },
    procedure: {
      width: '100%',
      textAlign: 'center',
      borderBottom: `1px solid ${color.$9}`,
      lineHeight: '0.1em',
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      letterSpacing: 'normal',
      color: color.$11,
      '& > span': {
        padding: '0 16px',
        background: color.$7,
      },
    },
    condition: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& > img': {
        marginRight: 4,
      },
    },
    conditionTooltip: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    index: {
      borderRadius: 20,
      background: color.$6,
      color: color.$7,
      fontSize: '13px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      textAlign: 'center',
      height: 20,
      padding: '4px 8px',
      marginLeft: 4,
    },
    line: {
      flexGrow: 1,
      borderBottom: `1px solid ${color.$9}`,
    },
    text: {
      margin: '0 16px',
      maxWidth: '80%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      fontSize: '13px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      letterSpacing: 'normal',
      color: color.$11,
    },
    order: { display: 'flex', alignItems: 'center' },
  },
  {
    name: 'PreviewConditionNode',
  },
);

const PreviewConditionNode = ({ node }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const nodes = useSelector(store => store.ma.data?.nodes) || [];
  const { type, buttons, groupId } = node?.data || {};

  const groupNode = useMemo(
    () => nodes.find(n => n?.id === groupId) || {},
    [nodes],
  );
  const { timeType, time } = groupNode?.data || {};

  const timeText = useMemo(() => {
    switch (timeType) {
      case TriggerTimeType.HOUR:
        const hour = parseInt(((time / 60) % 1440) / 60, 10);
        return t('condition-node.hour', { time: hour });
      case TriggerTimeType.DAY:
      default:
        const day = parseInt(time / 60 / 1440, 10);
        return t('condition-node.day', { time: day });
    }
  }, [timeType, time]);

  return (
    <div className={classes.root}>
      <div className={classes.order}>
        <span className={classes.line} />
        {type === 'click' &&
        typeof buttons === 'object' &&
        (buttons || []).length ? (
          <Tooltip
            light
            placement="top"
            isComponent
            title={
              <div className={classes.conditionTooltip}>
                {typeof buttons === 'object' &&
                  (buttons || []).map(b => (
                    <div key={`button-${b?.id}`} className={classes.index}>
                      {b?.index}
                    </div>
                  ))}
              </div>
            }
          >
            <span className={classes.text}>
              <div className={classes.condition}>
                {timeText}
                {t(`condition-node.${type}`)}
                {typeof buttons === 'object' &&
                  (buttons || []).slice(0, 2).map(b => (
                    <div key={`button-${b?.id}`} className={classes.index}>
                      {b?.index}
                    </div>
                  ))}
                {typeof buttons === 'object' && (buttons || []).length > 2 && (
                  <div style={{ marginLeft: 4 }}>...</div>
                )}
              </div>
            </span>
          </Tooltip>
        ) : (
          <span className={classes.text}>
            <div className={classes.condition}>
              {timeText}
              {t(`condition-node.${type}`)}
            </div>
          </span>
        )}
        <span className={classes.line} />
      </div>
    </div>
  );
};

export default PreviewConditionNode;
